import moment from 'moment';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Sidebar } from 'primereact/sidebar';
import { useEffect, useMemo, useState } from 'react';
import Tag from '../../../../components/Display/Tag';
import FormInput from '../../../../components/Form/FormInput';
import RichTextEditor from '../../../../components/Form/RichTextEditor';
import PropertyField from '../../../../components/Properties/PropertyField';
import { CONSTANT } from '../../../../utils/constants';
import { validateURL, validateYoutubeURL } from '../../../../utils/stringHelper';
import ImageForm from '../../common/image/ImageForm';

function EventManager({ isOpen, event, onUpdate, loading, onCreate, onClose, duplicationMode }) {
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState(undefined);
  const [endDate, setEndDate] = useState(undefined);
  const [place, setPlace] = useState('');
  const [organizer, setOrganizer] = useState('');
  const [description, setDescription] = useState('');
  const [timetable, setTimetable] = useState('');
  const [type, setType] = useState('');
  const [mainTag, setMainTag] = useState('');
  const [otherTags, setOtherTags] = useState([]);
  const [price, setPrice] = useState('');
  const [isDraft, setIsDraft] = useState(true);
  const [image, setImage] = useState(undefined);

  const [videoUrl, setVideoUrl] = useState(undefined);
  const [externalUrl, setExternalUrl] = useState('');
  const [labelUrl, setLabelUrl] = useState('');

  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setStartDate(new Date(event.start_dt));
      setEndDate(new Date(event.end_dt));
      setPlace(event.place);
      setOrganizer(event.organizer);
      setDescription(event.description);
      setTimetable(event.timetable);
      setType(event.type);
      setMainTag(event.main_tag);
      setOtherTags(
        event.other_tags.filter((ot) => ot.type === 'bac_other_tags').map((ot) => ot.id)
      );
      setPrice(event.price);
      setImage(event.image);
      setVideoUrl(event.external_video_url);
      setExternalUrl(event.external_url);
      setLabelUrl(event.label_url);
      setIsDraft(event.is_draft);
    } else {
      setTitle('');
      setStartDate(undefined);
      setEndDate(undefined);
      setPlace('');
      setOrganizer('');
      setDescription('');
      setTimetable('');
      setType('');
      setMainTag('');
      setOtherTags([]);
      setPrice('');
      setImage(undefined);
      setVideoUrl(undefined);
      setExternalUrl('');
      setLabelUrl('');
      setIsDraft(true);
    }
  }, [event]);

  const handleValidate = () => {
    const eventPayload = {
      title,
      start_dt: startDate,
      end_dt: endDate,
      place,
      organizer,
      description,
      timetable,
      type,
      main_tag: mainTag,
      other_tags: otherTags,
      price,
      image: image?.id,
      external_video_url: videoUrl,
      external_url: externalUrl,
      label_url: labelUrl,
      is_draft: isDraft
    };
    if (duplicationMode || !event) {
      onCreate(eventPayload);
    } else {
      onUpdate(eventPayload);
    }
  };

  const formIsValid = () => {
    return title.length > 3 && startDate && endDate && place && image && validateButtonUrl;
  };

  const validateButtonUrl = useMemo(
    () =>
      validateURL(externalUrl) &&
      ((labelUrl?.length > 0 && externalUrl?.length > 0) ||
        (!externalUrl?.length && !labelUrl?.length)),
    [externalUrl, labelUrl]
  );

  const isMultipleDay = useMemo(
    () => moment(startDate).format('L') !== moment(endDate).format('L'),
    [startDate, endDate]
  );

  const statusTemplate = (option) => <Tag tag={{ label: option.label, bgColor: option.bgColor }} />;

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={`Valider la ${!event || duplicationMode ? 'création' : 'modification'}`}
        className="w-full"
        onClick={handleValidate}
        severity="success"
        disabled={!formIsValid()}
        loading={loading}
      />
    </div>
  );

  return (
    <Sidebar
      style={{ width: 700 }}
      visible={isOpen}
      position="right"
      onHide={onClose}
      header={
        <h3 className="text-slate-800 text-2xl font-medium">{`${!event || duplicationMode ? 'Ajouter' : 'Modifier'} un Évènement`}</h3>
      }>
      <div className="sidebar-content-container">
        <div className="form-wrapper">
          <FormInput key="title" label={"Titre de l'évènement"} required isValid={title.length > 3}>
            <InputText
              id="title"
              value={title}
              maxLength={300}
              placeholder="Veuillez saisir le titre de l'évènement"
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormInput>

          <FormInput key="place" label={"Statut de l'évènement"}>
            <Dropdown
              value={isDraft}
              options={CONSTANT.shared.draftTypes}
              optionLabel="label"
              onChange={(e) => setIsDraft(e.value)}
              className="grow"
              valueTemplate={statusTemplate}
              itemTemplate={statusTemplate}
            />
          </FormInput>
          <div className="grid grid-cols-2 gap-2">
            <FormInput key="start_dt" label={'Date de début'} required isValid={startDate}>
              <Calendar
                id="start_dt"
                onChange={(e) => setStartDate(e.value)}
                placeholder="Selectionnez une date"
                value={startDate}
                locale="fr"
                showTime
                hourFormat="24"
                stepMinute={15}
              />
            </FormInput>
            <FormInput key="end_dt" label={'Date de fin'} required isValid={endDate}>
              <Calendar
                id="end_dt"
                onChange={(e) => setEndDate(e.value)}
                placeholder="Selectionnez une date"
                value={endDate}
                locale="fr"
                showTime
                hourFormat="24"
                stepMinute={15}
                minDate={startDate}
              />
            </FormInput>
          </div>

          {isMultipleDay && (
            <FormInput key="timetable" label={'Horaires, si événement sur plusieurs jours'}>
              <InputText
                id="timetable"
                value={timetable}
                maxLength={300}
                placeholder="Si l'événement est sur plusieurs jours, veuillez saisir les horaires"
                onChange={(e) => setTimetable(e.target.value)}
              />
            </FormInput>
          )}
          <FormInput key="price" label={"Prix de l'événement"} helpMessage="Max. 50 caractères">
            <InputText
              id="price"
              value={price}
              maxLength={50}
              placeholder="Veuillez saisir le prix de l'événement"
              onChange={(e) => setPrice(e.target.value)}
            />
          </FormInput>

          <FormInput key="place" label={"Lieu de l'évènement"} required isValid={place.length > 3}>
            <Dropdown
              value={place}
              options={CONSTANT.bassecour.places}
              optionLabel="label"
              onChange={(e) => setPlace(e.value)}
              placeholder="Indiquez le lieu de l'évènement"
              className="grow"
            />
          </FormInput>
          <FormInput key="organizer" label={"Organisateur de l'évènement"}>
            <InputText
              id="organizer"
              value={organizer}
              maxLength={300}
              placeholder="Veuillez saisir l'organisateur de l'évènement"
              onChange={(e) => setOrganizer(e.target.value)}
            />
          </FormInput>
          <FormInput key="type" label={"Type d'événement"} required isValid={type.length > 2}>
            <Dropdown
              value={type}
              options={CONSTANT.bassecour.type}
              optionLabel="label"
              onChange={(e) => setType(e.value)}
              placeholder="Indiquez le type d'évènement"
              className="grow"
            />
          </FormInput>
          <FormInput
            key="public"
            label={"Public de l'événement"}
            required
            isValid={mainTag.length > 3}>
            <Dropdown
              value={mainTag}
              options={CONSTANT.bassecour.main_tag}
              optionLabel="label"
              onChange={(e) => setMainTag(e.value)}
              placeholder="Indiquez le public de l'évènement"
              className="grow"
            />
          </FormInput>
          <FormInput
            label={'Bouton'}
            errorMessage={
              !validateURL(externalUrl)
                ? "L'URL doit commencer par http:// ou https://"
                : !validateButtonUrl
                  ? 'Le bouton doit avoir un Url et un label'
                  : ''
            }>
            <div className="flex flex-row gap-x-3">
              <InputText
                placeholder="Texte du bouton"
                value={labelUrl}
                onChange={(e) => setLabelUrl(e.target.value)}
                className="grow"
              />

              <InputText
                placeholder="https:// ou http://"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                className="grow"
                invalid={!validateURL(externalUrl)}
              />
            </div>
          </FormInput>

          <FormInput label={'Autres tags'}>
            <PropertyField
              type={'bac_other_tags'}
              selectedPropertyList={otherTags}
              setSelectedPropertyList={setOtherTags}
              scope={'bassecour'}
              isAllowedToCreate
            />
          </FormInput>
          <FormInput
            label={'Url de la vidéo Youtube'}
            helpMessage={'Utiliser la fonction "Partager la vidéo" de Youtube'}
            errorMessage={
              !validateYoutubeURL(videoUrl) &&
              "L'URL doit avoir la forme https://youtu.be/HSQq_zEeEcM?si=4qk_KCcecxC95Zq"
            }>
            <InputText
              id="video_url"
              value={videoUrl}
              maxLength={300}
              placeholder="Veuillez saisir l'url de la vidéo Youtube"
              onChange={(e) => setVideoUrl(e.target.value)}
              invalid={!validateURL(videoUrl)}
            />
          </FormInput>

          <FormInput key="description" label={'Description'}>
            <RichTextEditor
              value={description}
              onChange={(value) => {
                setDescription(value);
              }}
              acceptHeaders
              acceptImage
            />
          </FormInput>
          <ImageForm
            image={image}
            setImage={setImage}
            ratio={1.91}
            deleteImage={() => setImage(undefined)}
          />
        </div>
        {footerButtons}
      </div>
    </Sidebar>
  );
}

export default EventManager;
